import React, { useState, useEffect } from 'react';
import {
    HouseLine, Camera, PaperPlaneTilt, ArrowRight, ArrowLeft, X, Sparkle, Question,
} from '@phosphor-icons/react';

const STORAGE_KEY = 'defectr:tour:v1:dismissed';

const STEPS = [
    {
        icon: HouseLine,
        eyebrow: 'Step 1 of 3',
        title: 'Add your first property',
        body: (
            <>
                Tap the <strong>+ Add property</strong> button to register your new build.
                You can give it a name like <em>"Plot 134 — Oak Drive"</em> and an optional address.
                Each property holds your rooms, defects, floorplans and report.
            </>
        ),
    },
    {
        icon: Camera,
        eyebrow: 'Step 2 of 3',
        title: 'Capture defects, room by room',
        body: (
            <>
                Inside a property, add rooms then capture each defect with a <strong>title, photo and notes</strong>.
                Upload a <strong>floorplan</strong> from the Plan tab to drop pins exactly where each issue is.
                Got photo evidence and a vague hunch? Hit <strong>✨ AI: What's this?</strong> for an instant verdict
                with severity, likely cause and even an NHBC reference.
            </>
        ),
    },
    {
        icon: PaperPlaneTilt,
        eyebrow: 'Step 3 of 3',
        title: 'Send it to your builder',
        body: (
            <>
                When you're ready, head to the <strong>Report</strong> tab. You can:
                <ul className="list-disc pl-5 mt-2 space-y-1">
                    <li>Download a branded PDF with photos, AI verdicts and pin-overlaid floorplans</li>
                    <li>Email it directly to your builder from <code className="text-[11px] bg-[#F4F4F5] px-1">reports@defectr.co.uk</code></li>
                    <li>Generate a live <strong>share link</strong> — your builder can mark items "Fixed" and reply with a QR code</li>
                </ul>
            </>
        ),
    },
];

/** First-time onboarding tour. Renders a sequence of welcome modals.
 *  Dismissal is persisted in localStorage so it never re-fires for a
 *  given browser. The Help drawer exposes a "Restart tour" link that
 *  clears the flag and re-opens this component on demand. */
export default function OnboardingTour({ forceOpen = false, onClose }) {
    const [open, setOpen] = useState(false);
    const [step, setStep] = useState(0);

    useEffect(() => {
        if (forceOpen) { setOpen(true); setStep(0); return; }
        // Defer the first-time show so the rest of the app paints first
        // and the user doesn't get hit by a modal before the page lands.
        const t = setTimeout(() => {
            try {
                if (!localStorage.getItem(STORAGE_KEY)) setOpen(true);
            } catch { /* private mode etc — never block UI */ }
        }, 600);
        return () => clearTimeout(t);
    }, [forceOpen]);

    // Esc closes
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === 'Escape') dismiss(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const dismiss = () => {
        try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* noop */ }
        setOpen(false);
        onClose?.();
    };

    const next = () => {
        if (step < STEPS.length - 1) setStep(s => s + 1);
        else dismiss();
    };
    const prev = () => { if (step > 0) setStep(s => s - 1); };

    if (!open) return null;
    const cur = STEPS[step];
    const Icon = cur.icon;
    const isLast = step === STEPS.length - 1;

    return (
        <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-[2px] no-print"
            data-testid="onboarding-tour"
        >
            <div
                className="relative w-full max-w-md bg-white border-2 border-[#1FA5CF] shadow-2xl animate-in fade-in zoom-in duration-200"
                role="dialog"
                aria-labelledby="tour-title"
            >
                {/* Skip button */}
                <button
                    type="button"
                    onClick={dismiss}
                    data-testid="tour-skip"
                    className="absolute top-3 right-3 p-1.5 hover:bg-[#F4F4F5] active:scale-95 transition-all duration-150 z-10"
                    aria-label="Skip tour"
                >
                    <X size={18} weight="bold" />
                </button>

                {/* Cyan splash header */}
                <div className="bg-gradient-to-br from-[#1FA5CF] to-[#1787AB] text-white px-6 pt-6 pb-5">
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] opacity-80 mb-2">{cur.eyebrow}</div>
                    <div className="flex items-start gap-3">
                        <div className="flex-none w-12 h-12 bg-white/15 border border-white/30 flex items-center justify-center">
                            <Icon size={24} weight="bold" />
                        </div>
                        <h2 id="tour-title" className="font-display font-bold text-2xl leading-tight">{cur.title}</h2>
                    </div>
                </div>

                <div className="px-6 py-5 text-sm text-[#09090B] leading-relaxed">
                    {cur.body}
                </div>

                {/* Footer: progress dots + nav */}
                <div className="px-6 py-4 border-t border-[#E4E4E7] flex items-center justify-between gap-3">
                    <div className="flex items-center gap-1.5" aria-hidden>
                        {STEPS.map((_, i) => (
                            <span
                                key={i}
                                className={`h-1.5 rounded-full transition-all duration-200 ${i === step ? 'w-6 bg-[#1FA5CF]' : 'w-1.5 bg-[#D4D4D8]'}`}
                            />
                        ))}
                    </div>
                    <div className="flex items-center gap-2">
                        {step > 0 && (
                            <button
                                type="button"
                                onClick={prev}
                                data-testid="tour-prev"
                                className="inline-flex items-center gap-1 px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-[#52525B] hover:text-[#09090B] hover:bg-[#F4F4F5] active:scale-95 transition-all duration-150"
                            >
                                <ArrowLeft size={14} weight="bold" /> Back
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={next}
                            data-testid="tour-next"
                            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#1FA5CF] hover:bg-[#1787AB] text-white font-mono text-[11px] uppercase tracking-wider active:scale-95 transition-all duration-150"
                        >
                            {isLast ? <>Got it <Sparkle size={14} weight="fill" /></> : <>Next <ArrowRight size={14} weight="bold" /></>}
                        </button>
                    </div>
                </div>

                {/* Closer hint on the last step */}
                {isLast && (
                    <div className="px-6 pb-4 -mt-2 text-xs text-[#52525B] flex items-center gap-1.5">
                        <Question size={12} weight="bold" /> Need this again? Tap the help icon in the top-right any time.
                    </div>
                )}
            </div>
        </div>
    );
}

/** Imperatively restart the tour. Used by the Help drawer's
 *  "Replay welcome tour" link. */
export function restartOnboardingTour() {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
    // Force a one-tick state push so the tour component re-evaluates
    window.dispatchEvent(new Event('defectr:tour:restart'));
}
